import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import FitlabLogo from '@/components/brand/FitlabLogo'
import ContactButton from '@/components/ui/ContactButton'
import { cn } from '@/lib/utils'

type NavItem = { label: string; href: string }

const NAV_ITEMS: NavItem[] = [
  { label: 'Inicio', href: '/#inicio' },
  { label: 'Cómo funciona', href: '/#como-funciona' },
  { label: 'Beneficios', href: '/#beneficios' },
  { label: 'Nosotros', href: '/#nosotros' },
]

/**
 * Header sticky con glassmorphism al scroll.
 * Nav items renombrados según mockup oficial FITLAB.
 * CTA "Contacto" verde (mailto) replicado del mockup.
 */
export default function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileOpen])

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:rounded-lg focus:bg-fitlab-blue focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg"
      >
        Saltar al contenido principal
      </a>

      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-all duration-300',
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-fitlab-neutral-100'
            : 'bg-transparent',
        )}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="flex items-center gap-1 rounded-lg focus-visible:outline-none"
            aria-label="Ir a inicio FITLAB"
          >
            <FitlabLogo
              variant="horizontal"
              tone={scrolled ? 'color' : 'white'}
              role="img"
              className="h-10 w-auto transition-[filter] duration-300 sm:h-12"
            />
          </Link>

          <nav aria-label="Principal" className="hidden items-center gap-1 lg:flex">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={cn(
                  'rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
                  scrolled
                    ? 'text-fitlab-blue-hover hover:bg-fitlab-neutral-50 hover:text-fitlab-blue'
                    : 'text-white/95 hover:bg-white/10 hover:text-white',
                )}
              >
                {item.label}
              </a>
            ))}
            <a
              href="#contacto"
              className={cn(
                'rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
                scrolled
                  ? 'text-fitlab-blue-hover hover:bg-fitlab-neutral-50 hover:text-fitlab-blue'
                  : 'text-white/95 hover:bg-white/10 hover:text-white',
              )}
            >
              Contacto
            </a>
            <ContactButton size="sm" channel="whatsapp" withIcon={false} className="ml-3">
              Contacto
            </ContactButton>
          </nav>

          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className={cn(
              'rounded-lg p-2 transition-colors lg:hidden',
              scrolled
                ? 'text-fitlab-blue-hover hover:bg-fitlab-neutral-100'
                : 'text-white hover:bg-white/10',
            )}
            aria-label="Abrir menú"
            aria-expanded={mobileOpen}
            aria-controls="mobile-drawer"
          >
            <Menu className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.2 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 z-[55] bg-fitlab-neutral-900/40 backdrop-blur-sm lg:hidden"
              aria-hidden="true"
            />
            <motion.aside
              id="mobile-drawer"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{
                type: reduceMotion ? 'tween' : 'spring',
                stiffness: 300,
                damping: 30,
                duration: reduceMotion ? 0 : undefined,
              }}
              className="fixed inset-y-0 right-0 z-[56] w-80 max-w-[85vw] bg-white px-6 py-6 shadow-2xl lg:hidden"
              role="dialog"
              aria-modal="true"
              aria-label="Menú de navegación"
            >
              <div className="mb-8 flex items-center justify-between">
                <FitlabLogo variant="horizontal" role="img" className="h-10 w-auto" />
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg p-2 text-fitlab-neutral-900 hover:bg-fitlab-neutral-100"
                  aria-label="Cerrar menú"
                >
                  <X className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
                </button>
              </div>
              <nav aria-label="Principal móvil" className="flex flex-col gap-1">
                {NAV_ITEMS.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-lg px-3 py-3 text-base font-medium text-fitlab-neutral-900 transition-colors hover:bg-fitlab-neutral-50 hover:text-fitlab-blue"
                  >
                    {item.label}
                  </a>
                ))}
                <a
                  href="#contacto"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg px-3 py-3 text-base font-medium text-fitlab-neutral-900 transition-colors hover:bg-fitlab-neutral-50 hover:text-fitlab-blue"
                >
                  Contacto
                </a>
                <div className="mt-4" onClick={() => setMobileOpen(false)}>
                  <ContactButton size="md" channel="whatsapp" className="w-full">
                    Contacto
                  </ContactButton>
                </div>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
